import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, ArrowRight } from 'lucide-react';
import ProfileForm from './ProfileForm';
import { markProfileCreated } from '../services/profileService';

// ─── Step 1 — Welcome ─────────────────────────────────────────────────────────

function StepWelcome({ username, onNext, onClose }) {
  return (
    <div className="flex flex-col h-full">
      <div className="flex items-start justify-between mb-5">
        <div>
          <p className="text-xs font-semibold text-amber-500 uppercase tracking-widest mb-1">New feature</p>
          <h2 className="text-xl font-bold text-slate-900">Your public profile is here!</h2>
          <div className="w-12 h-1 bg-gradient-to-r from-amber-400 to-orange-400 rounded-full mt-2" />
        </div>
        <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 transition-colors">
          <X size={18} />
        </button>
      </div>

      <div className="flex-1 space-y-4 text-sm text-slate-600 leading-relaxed">
        <p>
          Hey <strong className="text-slate-800">@{username}</strong>! You've been publishing palettes to the gallery — and now it's time to put a face to your work.
        </p>
        <p>
          Your profile is a <strong className="text-slate-800">public page</strong> at{' '}
          <span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded text-slate-700">
            markersync.com/profile/{username}
          </span>{' '}
          where anyone can discover all your published palettes in one place.
        </p>
        <p>
          You can personalise it with a <strong className="text-slate-800">short bio</strong>, pick one of our <strong className="text-slate-800">20 unique avatars</strong>, and optionally link your <strong className="text-slate-800">TikTok</strong> or <strong className="text-slate-800">Instagram</strong> — so your fans can follow your content beyond MarkerSync.
        </p>
        <p>
          Everything is optional and you can update it anytime from your profile page. Ready to set it up?
        </p>
      </div>

      <div className="mt-6">
        <button
          onClick={onNext}
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold rounded-xl transition-colors"
        >
          Let's go <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
}

// ─── Modal shell ──────────────────────────────────────────────────────────────

export default function ProfileOnboardingModal({ username, uid, isOpen, onClose, onProfileCreated }) {
  const [step, setStep] = useState(1);

  useEffect(() => {
    if (isOpen) {
      setStep(1);
      document.documentElement.style.overflow = 'hidden';
      return () => { document.documentElement.style.overflow = ''; };
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handle = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handle);
    return () => document.removeEventListener('keydown', handle);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleDone = async (profileData) => {
    await markProfileCreated(uid);
    onProfileCreated(profileData);
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[60] backdrop-blur-sm bg-black/30 flex items-center justify-center p-4 animate-fadeOnly"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-2xl max-w-sm w-full shadow-2xl border border-slate-100 p-6 animate-slideUp flex flex-col" style={{ maxHeight: '90vh' }}>
        {step === 1 ? (
          <StepWelcome username={username} onNext={() => setStep(2)} onClose={onClose} />
        ) : (
          <>
            <div className="flex items-start justify-between mb-5">
              <div>
                <p className="text-xs font-semibold text-amber-500 uppercase tracking-widest mb-1">Step 2 of 2</p>
                <h2 className="text-xl font-bold text-slate-900">Set up your profile</h2>
                <div className="w-12 h-1 bg-gradient-to-r from-amber-400 to-orange-400 rounded-full mt-2" />
              </div>
              <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 transition-colors">
                <X size={18} />
              </button>
            </div>
            <ProfileForm
              username={username}
              onDone={handleDone}
              onBack={() => setStep(1)}
              saveLabel="Create profile"
            />
          </>
        )}
      </div>
    </div>,
    document.body
  );
}
